import type { PortalHelpConfig } from './portalHelpTypes';

export const PARTENAIRE_HELP: PortalHelpConfig = {
  pageTitle: 'Centre d’aide partenaire',
  pageDescription: 'Parrainage institutionnel et gestion de votre portefeuille.',
  roleLabel: 'Partenaire institutionnel',
  intro:
    'Votre espace est dédié au parrainage institutionnel et au suivi financier des commissions. Ce guide explique chaque écran pas à pas pour que vous puissiez piloter vos campagnes et vos retraits sans appeler le support à chaque étape.',
  gettingStarted: {
    title: 'Démarrage rapide',
    steps: [
      'Connectez-vous avec l’identifiant fourni lors de la convention de partenariat.',
      'Ouvrez Parrainage : repérez votre code institutionnel, votre lien et le QR code de campagne.',
      'Communiquez ce code à vos filleuls en leur rappelant de le saisir au moment de l’inscription (pas après).',
      'Suivez les inscriptions générées dans le tableau de bord parrainage.',
      'Consultez Portefeuille dès qu’une commission est validée et demandez un retrait selon les seuils affichés.',
    ],
  },
  sections: [
    {
      id: 'parrainage',
      title: 'Parrainage',
      icon: 'groups',
      summary:
        'Tableau de bord institutionnel : code partenaire, statistiques de filleuls, inscriptions en cours ou validées et outils de diffusion (lien, QR).',
      steps: [
        'Ouvrez Parrainage depuis le menu : votre code institutionnel unique s’affiche en haut — c’est la référence à communiquer officiellement.',
        'Copiez le lien de parrainage : il envoie le filleul vers la page d’inscription avec le code pré-rempli ou facile à saisir.',
        'Téléchargez ou imprimez le QR code pour affichage en point de vente, école partenaire ou événement.',
        'Le tableau liste chaque filleul : nom ou identifiant masqué selon confidentialité, date d’inscription, statut (en cours, validée, annulée).',
        'Les statistiques (nombre de filleuls, taux de conversion, commissions en attente) vous aident à mesurer vos campagnes.',
        'Les règles de commission (montant fixe, pourcentage, paliers) sont définies par l’administration dans votre convention — la plateforme applique ces règles automatiquement après validation du dossier filleul.',
        'Un filleul absent de la liste n’a probablement pas saisi votre code à l’inscription : il ne peut généralement pas être rattaché a posteriori.',
      ],
      tips: [
        'Intégrez le QR sur vos supports print pour réduire les erreurs de saisie manuelle.',
        'Formez vos équipes terrain : le code doit être mentionné avant le paiement des frais d’inscription.',
      ],
      linkTo: '/partenaire/parrainage',
      linkLabel: 'Espace parrainage',
    },
    {
      id: 'wallet',
      title: 'Portefeuille',
      icon: 'account_balance_wallet',
      summary:
        'Solde des commissions institutionnelles, historique détaillé des crédits et débits, et workflow de demande de retrait.',
      steps: [
        'Ouvrez Portefeuille : le solde disponible indique ce que vous pouvez retirer aujourd’hui (hors montants encore « en attente » de validation).',
        'L’historique distingue : commission filleul validée, ajustement administratif, retrait payé, retrait refusé.',
        'Avant un retrait, vérifiez le montant minimum et les frais éventuels indiqués sous le formulaire.',
        'Cliquez Demander un retrait, saisissez le montant, choisissez le canal de paiement autorisé (virement, Mobile Money…), confirmez.',
        'Le statut « En cours » signifie que la comptabilité du centre traite la demande — délai habituel : quelques jours ouvrés.',
        'Statut « Payé » : le virement a été effectué ; rapprochez avec votre relevé bancaire ou Mobile Money.',
        'Statut « Refusé » : lisez le motif (coordonnées incorrectes, montant trop faible, pièce manquante) corrigez et relancez.',
      ],
      tips: [
        'Une seule demande en cours à la fois évite les doublons de paiement.',
        'Conservez les exports ou captures d’historique pour votre comptabilité institutionnelle.',
      ],
      linkTo: '/partenaire/wallet',
      linkLabel: 'Mon portefeuille',
    },
    {
      id: 'notifications',
      title: 'Notifications',
      icon: 'notifications',
      summary:
        'Alertes en temps réel : nouvelle inscription filleul, commission validée, retrait traité ou message de l’administration.',
      steps: [
        'Cliquez sur la cloche en haut à droite pour l’aperçu des messages non lus.',
        'Ouvrez la page Notifications complète via le lien du panneau ou l’URL /partenaire/notifications.',
        'Marquez comme lu les messages traités pour garder une boîte claire.',
        'Activez les notifications push du navigateur si proposé — utile pour réagir vite aux inscriptions de campagne.',
        'Les emails parallèles peuvent arriver selon la configuration du centre : vérifiez aussi votre boîte mail institutionnelle.',
      ],
      tips: [
        'Une notification « commission validée » peut arriver plusieurs jours après le paiement du filleul : c’est normal si un délai de vérification est prévu.',
      ],
    },
  ],
  faqs: [
    {
      question: 'Comment sont calculées les commissions ?',
      answer:
        'Les barèmes (montant fixe, pourcentage du frais d’inscription, paliers par nombre de filleuls) sont configurés par l’administration dans Tarification / Parrainage selon votre convention. La plateforme calcule automatiquement après validation du paiement et du dossier du filleul. Pour un détail contractuel, référez-vous à votre accord signé avec le centre.',
    },
    {
      question: 'Un filleul a oublié de saisir notre code.',
      answer:
        'Le rattachement automatique n’est plus possible une fois l’inscription créée sans code. Contactez l’administration avec la preuve de votre prospection (date, nom du filleul) : une correction manuelle exceptionnelle peut être étudiée, sans garantie.',
    },
    {
      question: 'Notre solde semble incorrect.',
      answer:
        'Comparez l’historique Portefeuille avec la liste des filleuls validés dans Parrainage. Identifiez les lignes « en attente » vs « disponibles ». Communiquez au support les identifiants de filleul et dates concernées avant toute contestation.',
    },
  ],
};
